import React from "react";
import { Dialog, Box } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { modalStyles } from "./modalStyles";

/**
 * ConfirmDialog — Reusable small confirmation dialog
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onConfirm: () => void
 *   - title: string (e.g. "Delete User")
 *   - message: string (e.g. "Are you sure you want to delete this user?")
 *   - confirmLabel: string (default: "Confirm")
 *   - cancelLabel: string (default: "Cancel")
 *   - variant: "danger" | "warning" | "info" (default: "danger")
 *   - icon: string (MUI icon name, default based on variant)
 */
function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  icon,
}) {
  const variantConfig = {
    danger: { headerBg: "#E74C3C", icon: "warning", buttonSx: modalStyles.dangerButton },
    warning: { headerBg: "#E67E22", icon: "error_outline", buttonSx: { ...modalStyles.primaryButton, background: "#E67E22", "&:hover": { opacity: 0.9 } } },
    info: { headerBg: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)", icon: "help_outline", buttonSx: modalStyles.primaryButton },
  };

  const config = variantConfig[variant] || variantConfig.danger;
  const displayIcon = icon || config.icon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { ...modalStyles.paper, maxWidth: 400 } }}
    >
      {/* Header */}
      <MDBox
        sx={{
          ...modalStyles.header,
          background: config.headerBg,
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>{displayIcon}</Icon>
          <MDTypography sx={modalStyles.headerTitle}>{title}</MDTypography>
        </MDBox>
        <Box component="button" sx={modalStyles.closeButton} onClick={onClose}>
          ✕
        </Box>
      </MDBox>

      {/* Body */}
      <MDBox sx={{ ...modalStyles.body, py: 3 }}>
        <MDTypography sx={{ fontSize: 14, color: "#1E3A5F", lineHeight: 1.6 }}>
          {message}
        </MDTypography>
      </MDBox>

      {/* Footer */}
      <MDBox sx={modalStyles.footer}>
        <Box component="button" sx={modalStyles.secondaryButton} onClick={onClose}>
          {cancelLabel}
        </Box>
        <Box component="button" sx={config.buttonSx} onClick={onConfirm}>
          {confirmLabel}
        </Box>
      </MDBox>
    </Dialog>
  );
}

export default ConfirmDialog;
